import i18next from 'i18next';

import type { CodeConfig } from '@r6digital/rapidstor-sdk';
import { Site, corpCode, locationCode } from '@r6digital/rapidstor-sdk/domain';
import type { DomainTranslator, TextParams } from '@r6digital/rapidstor-sdk/domain';

import type Location from '@r6digital/rapidstor-types/react/entities/Location';

import { CorporationCode, getCorpTranslations } from '@/core/translations/helpers';

/**
 * Builds the SDK `Site` this app's screens read from.
 *
 * The SDK's `Site.from`/`hydrate` run the `rapidstor-types` transformers themselves, which assumes
 * the SDK made the fetch. This app transforms at its RTK Query boundary
 * (`src/core/api/rapidstor.ts`), so by the time a screen runs it already holds the friendly
 * entities — `Site.fromLocation` and `site.hydrateDescriptors` are the entry points for that case.
 */

/**
 * Maps the SDK's translation keys onto this app's i18next catalogue.
 *
 * The SDK names its user-visible strings with its own stable, library-neutral keys; i18next here
 * uses colon-namespaced snake_case. Only keys with an existing catalogue entry appear — anything
 * absent falls through to the SDK's own wording, which is exactly what a partial resolver is for.
 */
const I18N_KEYS: Readonly<Record<string, string>> = {
    'time.perMonthShort': 'time:per_month_short',
    'time.perWeekShort': 'time:per_week_short',
    'time.per4WeeksShort': 'time:per_4_weeks_short',
    'floor.ground': 'standardUnitListing:floor:ground',
    'floor.upper': 'standardUnitListing:floor:upper',
    'floor.driveUp': 'standardUnitListing:floor:driveUp',
};

/**
 * The `settings.*` keys whose wording a corporation may override, mapped to the field the corp
 * translation bundle holds it under.
 *
 * This reproduces the precedence `@/core/utils/rapidstor`'s text helpers implement by hand: a
 * corporation-level override wins over the operator's own setting.
 */
const CORP_SETTINGS_KEYS: Readonly<Record<string, string>> = {
    'settings.saveQuoteButtonText': 'saveQuoteButtonText',
    'settings.showPricesButtonText': 'showPricesButtonText',
    'settings.noAvailableUnitsText': 'noAvailableUnitsText',
    'settings.payNowButtonText': 'payNowButtonText',
    'settings.payLaterButtonText': 'payLaterButtonText',
};

/**
 * Resolves a corporation-level override for one of the SDK's settings keys.
 *
 * @param key - The SDK translation key.
 * @param corp - The corporation code whose overrides to consult.
 * @returns The override, or `undefined` when the corporation has none.
 */
const corpOverride = (key: string, corp: string): string | undefined => {
    const field = CORP_SETTINGS_KEYS[key];
    if (!field) return undefined;

    // The corp bundles are a union of differing shapes, so narrow before reaching in — the same
    // `in` check `@/core/utils/rapidstor`'s text helpers use.
    const translations = getCorpTranslations(corp as CorporationCode);
    if (!translations || !('rapidstor' in translations)) return undefined;

    const value = (translations.rapidstor as Record<string, unknown>)[field];
    return typeof value === 'string' && value.trim() ? value : undefined;
};

/**
 * Builds the {@link DomainTranslator} the SDK resolves its user-visible strings through.
 *
 * Deliberately partial: it answers the keys this app has wording for and returns the SDK's own
 * fallback for the rest, so a key the catalogue has not caught up with degrades to English rather
 * than to nothing.
 *
 * @param location - The friendly location entity, for its corporation code.
 * @returns The translator to bind to the domain context.
 */
export const translatorFor = (location: Location): DomainTranslator => (
    key: string,
    fallback: string,
    params?: TextParams,
): string => {
    const override = corpOverride(key, location.corpCode);
    if (override) return override;

    const i18nKey = I18N_KEYS[key];
    if (!i18nKey) return fallback;

    return i18next.t(i18nKey, { defaultValue: fallback, ...params });
};

/**
 * Builds the branded corp/site codes every domain object needs from the location itself.
 *
 * @param location - The friendly location entity.
 * @returns The validated {@link CodeConfig}.
 */
export const codesFor = (location: Location): CodeConfig => ({
    corpCode: corpCode(location.corpCode),
    locationCode: locationCode(location.locationCode),
});

/**
 * Wraps an already-transformed location as an SDK {@link Site}, with this app's translator bound.
 *
 * @param location - The friendly location entity.
 * @returns The `Site` wrapper.
 */
export const siteFor = (location: Location): Site => Site.fromLocation(location, codesFor(location), { translate: translatorFor(location) });
